import {Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, FormControl, InputLabel, Select, MenuItem, InputAdornment, CircularProgress, Alert, Box, Typography, Grid} from '@mui/material';
import { useEffect, useState } from 'react';
import {ConferenceHotelRoom} from '../../types/conferenceHotelRoom';
import { ConferenceHotel } from '@/src/types/conferenceHotel';
import { APIHOST } from '../../common';

export interface EditConferenceHotelRoomDialogProps {
  conferenceHotelId: string | null; // ID of the selected conference hotel
  conferenceHotelRoomId: string | null; // null for adding new room, string for editing existing room
  handleCloseDialog: () => void;
  open: boolean;
}  



export const EditConferenceHotelRoomDialog = ({ conferenceHotelId, conferenceHotelRoomId, handleCloseDialog, open }: EditConferenceHotelRoomDialogProps) => {

  const [conferenceHotelRoom, setConferenceHotelRoom] = useState<any>(null);
  const [conferenceHotel, setConferenceHotel] = useState<ConferenceHotel | null>(null);
  const [loading, setLoading] = useState(true);


  useEffect(() => {
    
    const loaders = [];
    loaders.push(

      //load hotel data to get date range for allocations
      fetch(`${APIHOST}/api/conference-hotels/${conferenceHotelId}?populate=hotel`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('authToken')}`
          } 
        })
        .then(response => response.json())
        .then(data => {
          console.log('Fetched conference hotel data:', data);
          const hotelData = data.data;

          if (!conferenceHotelRoomId && hotelData) {
         
            setConferenceHotelRoom({
              name: '',
              dailyRate: 0,
              maxOccupants: 2,
              conference_hotel: conferenceHotelId,
              bedCount: 2,
              allocations: generateDateRange(hotelData)
            });
          }
          setConferenceHotel(hotelData);
        })
        .catch(error => {
          console.error('Error fetching conference hotel data:', error);
        })
      );
    
    //load room data if editing existing room
    if (conferenceHotelRoomId) {
      // Fetch existing room data and populate form
      loaders.push(
        fetch(`${APIHOST}/api/conference-hotel-rooms/${conferenceHotelRoomId}`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('authToken')}`
          }
        })
          .then(response => response.json())
          .then(data => {
            setConferenceHotelRoom(data.data);
          })
          .catch(error => {
            console.error('Error fetching room data:', error);
          })
      );
    }

    Promise.all(loaders).catch(error => {
      console.error('Error loading data:', error);
    }).finally(() => {
  
      console.log('CF',conferenceHotel);
  
      setLoading(false);
    });
  }, []);

  const handleFormChange = (field: keyof ConferenceHotelRoom, value: any) => {
    setConferenceHotelRoom((prev: any) => prev ? ({ ...prev, [field]: value }) : null);
  };

  const handleAllocationChange = (date: string, quantity: number) => {
    setConferenceHotelRoom((prev: { allocations: any; }) => ({
      ...prev,
      allocations: {
        ...prev?.allocations,
        [date]: quantity
      }
    }));
  };

  // Generate date range based on conference hotel availability
  const generateDateRange = (hotel: ConferenceHotel): Record<string, number> => {
    if (!hotel) return {};

    const startDate = new Date(hotel.earliestAvailable);
    const endDate = new Date(hotel.latestAvailable);
    const allocationsMap: Record<string, number> = {};

    // Generate all dates in range
    for (let d = new Date(startDate); d <= endDate; d.setDate(d.getDate() + 1)) {
      allocationsMap[d.toISOString().split('T')[0]] = 0; // yyyy-mm-dd format
    }

    return allocationsMap;
  };

  const handleFormSubmit = () => {
    if (!conferenceHotelRoom) return;

    // Validate that at least one allocation has a quantity > 0
    const hasValidAllocations = Object.values(conferenceHotelRoom?.allocations || {}).some(quantity=> quantity as number > 0);
    if (!hasValidAllocations) {
      alert('Please set room quantities for at least one date');
      return;
    }

    const method = conferenceHotelRoomId ? 'PUT' : 'POST';
    const url = conferenceHotelRoomId ? `${APIHOST}/api/conference-hotel-rooms/${conferenceHotelRoomId}` : `${APIHOST}/api/conference-hotel-rooms`;

    const {id, documentId, createdAt, updatedAt, blockTotal, pendingCount, publishedAt, remainingAvailable, reservedTotal, ...conferenceHotelRoomData}= conferenceHotelRoom

    const roomData = {
      data: {
        ...conferenceHotelRoomData
      }
    };

    fetch(url, {
      method,
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('authToken')}`
      },
      body: JSON.stringify(roomData)
    })
    .then(response => {
      if (!response.ok) {
        throw new Error('Failed to save room');
      }
      return response.json();
    })
    .then(data => {
      handleCloseDialog();
    })
    .catch(error => {
      console.error('Error saving room:', error);
    });
  };

  if (loading) {
    return (
      <Dialog open={open} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Loading</DialogTitle>
        <DialogContent sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 200 }}>
          <CircularProgress />
        </DialogContent>
      </Dialog>
    );
  }

  return (
   
      <Dialog 
        open={open} 
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{conferenceHotelRoomId ? 'Edit Hotel Room' : 'Add New Hotel Room'}</DialogTitle>
        <DialogContent>
         
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            {conferenceHotel && (
              <Typography variant="subtitle1" color="textSecondary">
                {conferenceHotel.hotel.longName}
              </Typography>
            )}

            <TextField
              label="Room Name"
              value={conferenceHotelRoom?.name || ''}
              onChange={(e) => handleFormChange('name', e.target.value)}
              fullWidth
              required
              placeholder="e.g., Standard Double Room"
            />

            <TextField
              label="Daily Rate"
              type="number"
              value={conferenceHotelRoom?.dailyRate || 0}
              onChange={(e) => handleFormChange('dailyRate', e.target.value)}
              fullWidth
              required
              InputProps={{
                startAdornment: <InputAdornment position="start">$</InputAdornment>,
              }}
              inputProps={{
                min: 0,
                step: 0.01
              }}
              placeholder="0.00"
            />

            <TextField
              label="# of Beds"
              type="number"
              value={conferenceHotelRoom?.bedCount || 1}
              onChange={(e) => handleFormChange('bedCount', e.target.value)}
              fullWidth
              inputProps={{
                min: 1
              }}
              placeholder="1"
            />

            <TextField
              label="Max Occupants"
              type="number"
              value={conferenceHotelRoom?.maxOccupants || 1}
              onChange={(e) => handleFormChange('maxOccupants', e.target.value)}
              fullWidth
              inputProps={{
                min: 1
              }}
              placeholder="Optional"
              helperText="Maximum number of people per room (optional)"
            />

            {/* Room Allocations by Date */}
            {conferenceHotel && Object.keys(conferenceHotelRoom?.allocations || {}).length > 0 && (
              <Box py={2}>
                <Typography variant="h6" gutterBottom>
                  Room Allocations by Date
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Set the number of rooms available for each night
                </Typography>
                <Grid container spacing={2} pt={2} sx={{ maxHeight: 400, overflow: 'auto' }}>
                  {Object.entries(conferenceHotelRoom?.allocations || {})
                    .sort(([dateA], [dateB]) => dateA.localeCompare(dateB))
                    .map(([date, quantity]) => {
                      const formattedDate = new Date(date).toLocaleDateString('en-US', {
                        weekday: 'short',
                        month: 'short',
                        day: 'numeric'
                      });
                      return (
                        <Grid key={date} size={{ xs: 12, sm: 6, md: 4 }}>
                          <TextField
                            label={formattedDate}
                            type="number"
                            value={quantity}
                            onChange={(e) => handleAllocationChange(date, parseInt(e.target.value) || 0)}
                            fullWidth
                            size="small"
                            inputProps={{
                              min: 0
                            }}
                            helperText={date}
                          />
                        </Grid>
                      );
                    })
                  }
                </Grid>
                <Typography variant="body2" color="primary" sx={{ mt: 1 }}>
                  Total room nights: {Object.values(conferenceHotelRoom?.allocations || {}).reduce((sum: number, quantity) => sum + (quantity as number), 0)}
                </Typography>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} >
            Cancel
          </Button>
          <Button 
            onClick={handleFormSubmit} 
            variant="contained" 
            disabled={!conferenceHotelRoom?.name || !conferenceHotelRoom?.dailyRate || Object.values(conferenceHotelRoom?.allocations || {}).every(quantity => quantity === 0)}
          >
            {conferenceHotelRoomId ? 'Update Room' : 'Add Room'}
          </Button>
        </DialogActions>
      </Dialog>
  );
}